import React, { useEffect, useState, useMemo } from 'react';
import { chart } from '../../../assets/assets';
import { useGetWeeklyRevenue } from '../../../services/admin.stats.service';

const WeeklyRevenue = () => {
  const [animate, setAnimate] = useState(false);
  const { data: revenueData, isLoading } = useGetWeeklyRevenue();

  const weeklyData = useMemo(() => {
    if (!revenueData) return [];

    // Get the last 7 days including today
    const days = [];
    const now = new Date();
    
    for (let i = 6; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(d.getDate() - i);
      days.push({
        day: d.getDate(),
        month: d.getMonth() + 1,
        year: d.getFullYear(),
        amount: 0
      });
    }

    // Map real data to our 7-day window
    revenueData.forEach(item => {
      const found = days.find(d => d.day === item.day && d.month === item.month && d.year === item.year);
      if (found) {
        found.amount = item.amount;
      }
    });

    // Calculate scaling factor based on max amount
    const maxAmount = Math.max(...days.map(d => d.amount), 1); 
    
    return days.map(d => {
      const rawHeight = (d.amount / maxAmount) * 100;
      // If amount is 0, give it a tiny "touch of growth" (e.g. 5%)
      const height = d.amount === 0 ? 5 : Math.max(5, rawHeight);
      
      return {
        ...d,
        height
      };
    });
  }, [revenueData]);

  useEffect(() => {
    if (!isLoading && weeklyData.length > 0) {
      const timeout = setTimeout(() => setAnimate(true), 100);
      return () => clearTimeout(timeout);
    }
  }, [isLoading, weeklyData]);

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-[#1d2026] rounded-[20px] p-6 w-full max-w-3xl mx-auto h-[264px] flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-[#174568] dark:border-blue-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-[#1d2026] rounded-[20px] p-6 w-full max-w-3xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-800 dark:text-white">Weekly Revenue</h2>
          <p className="text-xs text-gray-500 dark:text-gray-400">Totals from completed orders</p>
        </div>
        <div className="w-6 h-6">
          <img src={chart} alt="Revenue Chart"/>
        </div>
      </div>

      <div className="flex items-end justify-between space-x-2 h-40">
        {weeklyData.map((data, index) => {
          return (
            <div key={index} className="flex flex-col items-center flex-1">
              <div className="group relative w-full flex flex-col items-center">
                {/* Tooltip */}
                <div className="absolute -top-10 left-1/2 -translate-x-1/2 bg-gray-800 dark:bg-gray-700 text-white text-[10px] px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity z-10 shadow-xl whitespace-nowrap pointer-events-none">
                  ${data.amount.toLocaleString()}
                </div>
                
                <div className="flex flex-col justify-end h-32 w-4 md:w-5 relative overflow-hidden">
                  <div
                    className="rounded-t bg-gradient-to-t from-indigo-500 to-purple-500 animate-bar"
                    style={{
                      height: animate ? `${data.height * 0.4}%` : '0px',
                      transition: 'height 0.8s ease',
                    }}
                  />
                  <div
                    className="bg-gradient-to-t from-sky-400 to-blue-400 animate-bar"
                    style={{
                      height: animate ? `${data.height * 0.35}%` : '0px',
                      transition: 'height 0.8s ease 0.1s',
                    }}
                  />
                  <div
                    className="rounded-b bg-gradient-to-t from-blue-100 to-blue-200 animate-bar"
                    style={{
                      height: animate ? `${data.height * 0.25}%` : '0px',
                      transition: 'height 0.8s ease 0.2s',
                    }}
                  />
                </div>
              </div>
              <span className="text-[10px] md:text-sm text-gray-500 dark:text-gray-400 mt-2 font-medium">{data.day}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default WeeklyRevenue;
